<template>
  <v-card class="mt-4 px-6 py-2 elevation-0">
    <v-row>
      <v-col>
        <v-row class="flex-nowrap">
          <v-col
            :class="`col-grow text-truncate${haveSelectedItem ? '' : ' mt-1'}`"
          >
            <v-expand-transition>
              <search-simple
                v-if="isEditingCard || !haveSelectedItem"
                :endpoint-excludes="['dashboard', 'organization', 'project']"
                :event-name="(haveSelectedItem && 'home/updateHome') || 'home/prepareToCreateHome'"
                :input-label="currentHomeInputLabel"
                :rules="[v => isFormValid || 'Report is required']"
                :selected-item="userSelectedReport"
                :show-all-results="false"
                text-key="textAlt"
                value-key="href"
              />

              <v-row
                v-else
                class="mt-3 px-3 font-weight-bold"
              >
                <v-col
                  :class="`pa-0 text-truncate ${titleColor}--text`"
                  :title="currentHomeTitle"
                >
                  <span>{{ currentHomeTitle }}</span>
                </v-col>
              </v-row>
            </v-expand-transition>
          </v-col>

          <v-col class="col-auto px-0">
            <v-card-actions class="pl-0">
              <v-btn
                v-if="!haveSelectedItem"
                class="px-4"
                color="primary"
                transition="scroll-y-transition"
                outlined
                @click.stop="createHomeAndResetForm(userSelectedReport)"
              >
                <span class="caption pl-1">
                  Add Card
                </span>
              </v-btn>

              <v-menu
                v-else-if="haveSelectedItem && currentHome"
                offset-y
                transition="slide-y-transition"
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    color="darkblue"
                    icon
                    small
                    v-on="on"
                  >
                    <v-icon>more_vert</v-icon>
                  </v-btn>
                </template>

                <v-list class="py-0">
                  <v-list-item
                    :to="currentHome.href"
                  >
                    <v-list-item-title class="subtitle-2">
                      <span>View Report</span>
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    @click="goToAdServer(currentHome.adServerUrl)"
                  >
                    <v-list-item-title class="subtitle-2">
                      <span>View Ad Server</span>
                    </v-list-item-title>
                  </v-list-item>

                  <v-divider />

                  <v-list-item
                    @click="editCard"
                  >
                    <v-list-item-title class="subtitle-2">
                      <span>{{ isEditingCard ? 'Done Editing' : 'Edit Card' }}</span>
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    @click="confirmDeleteHome(selectedItem)"
                  >
                    <v-list-item-title class="subtitle-2 toasted--text">
                      <span>Delete Card</span>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-card-actions>
          </v-col>
        </v-row>

        <v-row
          v-if="haveSelectedItem && currentHome"
          class="align-center caption mt-0 mb-0"
        >
          <v-col class="col-grow text-truncate">
            <v-skeleton-loader
              v-if="!analytics || !analytics.metricType"
              class="px-0 mt-2"
              :loading="true"
              type="text"
            />

            <v-list-item-title
              v-else
            >
              <span
                class="headline font-weight-medium"
              >
                {{ formatNumber(analytics.metricType.value, analytics[analytics.metricType.value]) }}
              </span>
            </v-list-item-title>

            <v-skeleton-loader
              v-if="!analytics || !analytics.metricType"
              class="px-0 mt-2"
              :loading="true"
              type="text"
            />

            <v-list-item-subtitle
              v-else
            >
              <span>{{ analytics.metricType.text }}</span>
              <span v-if="analytics.metricGroupBy">
                across
                <span class="font-weight-medium">
                  {{ formatNumber(analytics.metricGroupBy.value, analytics[analytics.metricGroupBy.value]) }}
                </span>
                {{ analytics.metricGroupBy.text }}
              </span>
            </v-list-item-subtitle>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import format from 'src/utils/format'
import { createObject } from 'src/utils/objects'
import HomeMixin from 'src/home/store/utils/home-mixin'

const { mapGetters: mapHomeGetters, mapState: mapHomeState } = createNamespacedHelpers('home')
const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'HomeCard',
  components: {
    SearchSimple: () => import('src/tools/components/search/SearchSimple'),
  },
  mixins: [
    HomeMixin,
  ],
  props: {
    selectedItem: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },
  data() {
    return {
      analytics: {},
      isEditingCard: false,
      userSelectedReport: null,
    }
  },
  computed: {
    ...mapHomeGetters([
      'setDefaultMetricSettings',
    ]),
    ...mapHomeState({
      metricGroupByMap: (state) => state.metricGroupByMap,
      metricTypeMap: (state) => state.metricTypeMap,
    }),
    ...mapLayoutGetters([
      'textColor',
      'titleColor',
    ]),
    ...mapToolsGetters([
      'findSearchOption',
      'getSearchOptions',
    ]),
    ...mapToolsState({
      searchResults: (state) => state.searchResults,
    }),
    haveSelectedItem() {
      const { selectedItem } = this
      return selectedItem && typeof selectedItem === 'object' && selectedItem.associationId
    },
    isSelectedItemAReport() {
      const { haveSelectedItem, selectedItem } = this
      const excludedEndpoints = ['dashboard']
      const notExcludedEndpoint = excludedEndpoints.indexOf(selectedItem.reportPath) === -1
      const endpointKey = selectedItem.baseReportName || selectedItem.routeName
      return haveSelectedItem && selectedItem.reportPath && notExcludedEndpoint && endpointKey
    },
    currentReport() {
      const { isSelectedItemAReport, selectedItem } = this
      const baseReportName = () => selectedItem.baseReportName || 'pacing'
      const reportPath = () => selectedItem.reportPath || 'all-data'
      const routeName = () => selectedItem.routeName || 'delivery'
      const makeDefaultReport = () => ({
        associationId: selectedItem.associationId,
        baseReportName: baseReportName(),
        index: selectedItem.index,
        reportPath: reportPath(),
        routeName: routeName(),
        subId: selectedItem.subId,
      })

      return (isSelectedItemAReport && selectedItem) || makeDefaultReport()
    },
    currentSearchOptions() {
      const { currentReport, getSearchOptions, searchResults } = this
      const endpointKey = currentReport.baseReportName || currentReport.routeName
      return getSearchOptions(endpointKey, searchResults)
    },
    currentSearchOption() {
      const { associationId, baseReportName, reportPath, routeName, subId } = this.currentReport
      return this.findSearchOption(this.currentSearchOptions, associationId, baseReportName, reportPath, routeName, subId)
    },
    currentHome() {
      return this.userSelectedReport || this.currentSearchOption
    },
    currentHomeTitle() {
      return (this.currentHome && this.currentHome.textAlt) || 'Loading ...'
    },
    currentHomeInputLabel() {
      return (this.currentHome && this.currentHome.textAlt) || 'Search reports ...'
    },
    metricType() {
      const { haveSelectedItem, metricTypeMap, selectedItem } = this
      const endpointKey = haveSelectedItem && selectedItem && (selectedItem.baseReportName || selectedItem.routeName)
      return (endpointKey && metricTypeMap[endpointKey]) || []
    },
    groupName() {
      const { metricGroupByMap, selectedItem } = this
      const endpointKey = selectedItem && (selectedItem.baseReportName || selectedItem.routeName)
      return (endpointKey && metricGroupByMap[endpointKey]) || []
    },
  },
  watch: {
    currentSearchOption: {
      deep: true,
      handler(newValue, oldValue) {
        const haveNewValue = newValue && newValue.associationId

        if (haveNewValue && this.hasChangedValue(newValue, oldValue)) {
          const endpointKey = newValue.baseReportName || newValue.routeName
          const { index } = this.currentReport || {}
          const userSelectedReport = createObject(this.currentSearchOption, { index })

          this.setDefaultMetricSettings(endpointKey, userSelectedReport)

          this.userSelectedReport = userSelectedReport
        }
      },
      immediate: true,
    },
    $route() {
      this.userSelectedReport = null
    },
    userSelectedReport: {
      deep: true,
      handler(newValue) {
        const haveNewValue = newValue && newValue.associationId

        if (haveNewValue) {
          this.updateAnalytics(newValue)
        }
      },
      immediate: true,
    },
  },
  methods: {
    editCard() {
      this.isEditingCard = !this.isEditingCard
    },
    goToAdServer(url) {
      window.open(url, '_blank')
    },
    formatNumber(metricType, value) {
      const formatMap = {
        totalHours: format.hhmm,
      }
      return typeof formatMap[metricType] === 'function' ? formatMap[metricType](value) : format.number(value)
    },
    async updateAnalytics(currentHome) {
      this.analytics = null

      const { associationId, baseReportName, networkCode, publisherId, reportPath, routeName, teamId, subId } = currentHome
      const params = { baseReportName, routeName }
      const query = { associationId, networkCode, publisherId, reportPath, subId, teamId }

      // Uses metricType on this HomeCard, not from home store's state, since the user will be able to change these values.
      const metricType = this.metricType[0]
      const metricGroupBy = this.groupName[0]
      const metric = { metricType, metricGroupBy }

      currentHome.metricGroupBy = metricGroupBy
      currentHome.metricType = metricType

      this.analytics = await this.$store.dispatch('home/getHomeCard', { metric, params, query })
    },
    hasChangedDeeply(newValue, oldValue) {
      return newValue && oldValue && (newValue.associationId !== oldValue.associationId
        || newValue.baseReportName !== oldValue.baseReportName
        || newValue.reportPath !== oldValue.reportPath
        || newValue.routeName !== oldValue.routeName
        || newValue.subId !== oldValue.subId)
    },
    hasChangedValue(newValue, oldValue) {
      const hasChangedType = (newValue === undefined || oldValue === undefined) && newValue !== oldValue
      return hasChangedType || this.hasChangedDeeply(newValue, oldValue)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~src/styles/lists.scss';
</style>
